import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth, useDriverSession, useDriverOffers, useActiveTrip } from '../../lib/driver-hooks';
import { Power, MapPin, Navigation, Check, X, Car, Package, CheckCircle2, LogOut, Loader2, Wifi, WifiOff, Bug, RefreshCw, ChevronDown, ChevronUp, Phone, MessageCircle, Map as MapIcon, Clock, CornerUpLeft, CornerUpRight, ArrowUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { EmbeddedMap } from '../../components/driver/EmbeddedMap';
import { StaticRouteMap } from '../../components/driver/StaticRouteMap';
import { NavigationMap } from '../../components/driver/NavigationMap';
import type { RouteStep } from '../../lib/routing';
// ─── Map Error Boundary — catches render crashes, falls back to simple map ───
class MapErrorBoundary extends Component<{
  children: React.ReactNode;
  fallbackLat: number | null;
  fallbackLng: number | null;
}, {
  hasError: boolean;
}> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error) {
    console.error('[MAP-BOUNDARY] Crash caught:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return <EmbeddedMap destinationLat={this.props.fallbackLat} destinationLng={this.props.fallbackLng} data-id="element-6854" data-mp="2zuwas1dz1419" />;
    }
    return this.props.children;
  }
}
// ─── Alarm System — loud repeating alert for new offers ──────────
// ─── Alarm System — loud repeating alert for new offers ──────────
// IMPORTANT: On mobile, AudioContext MUST be created/resumed during a user gesture.
// Call warmUp() on any user tap (e.g. "CONECTAR") to unlock audio playback.
function createAlarmAudio(): {
  play: () => void;
  stop: () => void;
  warmUp: () => void;
} {
  let audioCtx: AudioContext | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let isPlaying = false;
  // HTML5 Audio fallback for browsers where Web Audio API fails (some iOS versions)
  let fallbackAudio: HTMLAudioElement | null = null;
  function getCtx(): AudioContext | null {
    try {
      if (!audioCtx) {
        const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtxClass) return null;
        audioCtx = new AudioCtxClass();
      }
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }
      return audioCtx;
    } catch {
      return null;
    }
  }
  function beep() {
    const ctx = getCtx();
    if (!ctx) {
      // Fallback: use HTML5 Audio with a short beep tone (base64 WAV)
      try {
        if (!fallbackAudio) {
          // Tiny 440Hz beep WAV, 0.3s, base64 encoded
          fallbackAudio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVBBQQAIAAABAAEARAAA' + 'AEQAAABAAgAZGF0YUlvT19/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/');
        }
        fallbackAudio.currentTime = 0;
        fallbackAudio.volume = 1.0;
        fallbackAudio.play().catch(() => {});
      } catch {}
      return;
    }
    try {
      // First tone — high pitch alarm
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.type = 'square';
      osc1.frequency.setValueAtTime(880, ctx.currentTime);
      osc1.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      osc1.frequency.setValueAtTime(880, ctx.currentTime + 0.24);
      gain1.gain.setValueAtTime(0.7, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.4);
      // Second tone — slightly delayed, different pitch for urgency
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = 'square';
      osc2.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
      osc2.frequency.setValueAtTime(1100, ctx.currentTime + 0.3);
      gain2.gain.setValueAtTime(0, ctx.currentTime);
      gain2.gain.setValueAtTime(0.5, ctx.currentTime + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
      osc2.start(ctx.currentTime + 0.15);
      osc2.stop(ctx.currentTime + 0.45);
    } catch (e) {
      console.warn('[ALARM] beep failed:', e);
    }
  }
  return {
    // Call this on a user gesture (tap) to unlock audio on ALL mobile browsers
    warmUp() {
      try {
        // 1. Warm up Web Audio API
        const ctx = getCtx();
        if (ctx) {
          const buffer = ctx.createBuffer(1, 1, 22050);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);
          console.log('[ALARM] AudioContext warmed up, state:', ctx.state);
        }
        // 2. Warm up HTML5 Audio fallback
        if (!fallbackAudio) {
          fallbackAudio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVBBQQAIAAABAAEARAAA' + 'AEQAAABAAgAZGF0YUlvT19/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/');
        }
        fallbackAudio.volume = 0.01;
        fallbackAudio.play().then(() => {
          fallbackAudio!.pause();
          fallbackAudio!.volume = 1.0;
          console.log('[ALARM] HTML5 Audio fallback warmed up');
        }).catch(() => {});
        // 3. Warm up Speech Synthesis (required on iOS Safari)
        if ('speechSynthesis' in window) {
          const warmUtterance = new SpeechSynthesisUtterance('');
          warmUtterance.volume = 0;
          warmUtterance.lang = 'es-ES';
          window.speechSynthesis.speak(warmUtterance);
          // Pre-load voices
          window.speechSynthesis.getVoices();
          console.log('[ALARM] SpeechSynthesis warmed up');
        }
      } catch (e) {
        console.warn('[ALARM] warmUp failed:', e);
      }
    },
    play() {
      if (isPlaying) return;
      isPlaying = true;
      console.log('[ALARM] 🔊 Starting alarm');
      // Immediate first beep
      beep();
      // Repeat every 700ms for urgency
      intervalId = setInterval(() => {
        beep();
        // Vibrate (not supported on iOS Safari, but works on Android)
        if ('vibrate' in navigator) {
          navigator.vibrate([300, 100, 300, 100, 400]);
        }
      }, 700);
    },
    stop() {
      if (isPlaying) console.log('[ALARM] 🔇 Stopping alarm');
      isPlaying = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      // Stop vibration (Android only)
      if ('vibrate' in navigator) {
        navigator.vibrate(0);
      }
      // Stop any playing fallback audio
      if (fallbackAudio) {
        fallbackAudio.pause();
        fallbackAudio.currentTime = 0;
      }
    }
  };
}
// Request notification permission — must be called from user gesture on some browsers
function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then(perm => {
      console.log('[NOTIFY] Permission result:', perm);
    });
  }
}
// Show system notification
function showOfferNotification() {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification('🚗 ¡Nuevo viaje disponible!', {
        body: 'Tienes una nueva oferta de entrega. ¡Toca para aceptar!',
        tag: 'driver-offer',
        requireInteraction: true,
        vibrate: [300, 100, 300, 100, 400, 200, 500],
        silent: false
      } as any);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      setTimeout(() => notification.close(), 30000);
    } catch (e) {
      console.warn('[NOTIFY] Failed:', e);
    }
  }
}
const alarmInstance = createAlarmAudio();
function useOfferAlert(offersCount: number) {
  const prevCount = useRef(offersCount);
  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);
  useEffect(() => {
    if (offersCount > prevCount.current) {
      console.log('[ALARM] New offer detected! Count:', offersCount);
      // 1. Strong vibration pattern
      if ('vibrate' in navigator) {
        navigator.vibrate([300, 100, 300, 100, 400, 200, 500]);
      }
      // 2. Loud repeating alarm sound
      alarmInstance.play();
      // 3. System notification (works in background)
      showOfferNotification();
    }
    // Stop alarm when offers are cleared (accepted/rejected/expired)
    if (offersCount === 0 && prevCount.current > 0) {
      alarmInstance.stop();
    }
    prevCount.current = offersCount;
  }, [offersCount]);
  return {
    stopAlarm: alarmInstance.stop,
    warmUpAudio: alarmInstance.warmUp
  };
}
// ─── Countdown Timer Component ───────────────────────────────────
function CountdownTimer({
  expiresAt
}: {
  expiresAt: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      return Math.max(0, Math.floor(diff / 1000));
    };
    setSecondsLeft(calc());
    const interval = setInterval(() => setSecondsLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  const isUrgent = secondsLeft <= 10;
  const minutes = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  if (secondsLeft <= 0) {
    return <span className="text-red-400 font-bold text-lg" data-id="element-6855" data-mp="17bcg8jzwl56n">Expirada</span>;
  }
  return <div className={`text-center ${isUrgent ? 'animate-pulse' : ''}`} data-id="element-6856" data-mp="13994du9bnyku">
      <div className={`text-5xl font-black tabular-nums ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-6857" data-mp="13bc1szct0tzl">
        {minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`}
      </div>
      <p className="text-xs text-white/40 uppercase tracking-widest mt-1" data-id="element-6858" data-mp="3afukc3fkkm54">
        Tiempo restante
      </p>
    </div>;
}
// ─── Debug Panel Component ───────────────────────────────────────
function DriverDebugPanel({
  user,
  driverId,
  authDebug,
  offers,
  debugInfo,
  isOnline,
  onRefetch
}: {
  user: any;
  driverId: number | null;
  authDebug: any;
  offers: any[];
  debugInfo: any;
  isOnline: boolean;
  onRefetch: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="mx-5 mb-4" data-id="element-6859" data-mp="3ofdqiqotlnes">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between gap-2 bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-2.5 text-xs" data-id="element-6860" data-mp="3oh6ufy81ttj3">
        <div className="flex items-center gap-2" data-id="element-6861" data-mp="1u3fekgz4mdf7">
          <Bug className="w-3.5 h-3.5 text-yellow-400" data-id="element-6862" data-mp="9k3lwoc9k8xw" />
          <span className="text-yellow-400 font-bold" data-id="element-6863" data-mp="9klos3tflles">DEBUG</span>
          <span className="text-white/40" data-id="element-6864" data-mp="ehvbhbpfriyx">
            driver={driverId || 'null'} | offers={offers.length} | bypass=
            {debugInfo.allOffersCount}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-white/30" data-id="element-6865" data-mp="4rskfh1vkf38" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" data-id="element-6866" data-mp="3my6uh4x0w4wc" />}
      </button>

      {expanded && <div className="mt-1 bg-[#1A1A1A] border border-white/10 rounded-xl p-4 space-y-2 text-xs font-mono" data-id="element-6867" data-mp="3ry2zn7hgi2dh">
          <div className="text-white/60" data-id="element-6868" data-mp="3dzd4vyi40esa">
            <span className="text-white/30" data-id="element-6869" data-mp="3e0eia9mlldd6">auth.uid: </span>
            <span className="text-cyan-400" data-id="element-6870" data-mp="3i0evjqxyniin">{authDebug.userId || 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-6871" data-mp="1c802ph7j0kzx">
            <span className="text-white/30" data-id="element-6872" data-mp="1c4uee46q1n59">driverId: </span>
            <span className="text-cyan-400" data-id="element-6873" data-mp="1g6z08gqxhfhc">{driverId ?? 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-6874" data-mp="2p69ye79jyjhy">
            <span className="text-white/30" data-id="element-6875" data-mp="2p34d7341o1ri">RPC result: </span>
            <span className="text-cyan-400" data-id="element-6876" data-mp="2k5ck45rny9lq">
              {JSON.stringify(authDebug.rpcResult)}
            </span>
          </div>
          {authDebug.rpcError && <div className="text-red-400" data-id="element-6877" data-mp="2b5z7j488t4ws">
              <span className="text-white/30" data-id="element-6878" data-mp="2b70lbggn85i7">RPC error: </span>
              {JSON.stringify(authDebug.rpcError)}
            </div>}
          <div className="text-white/60" data-id="element-6879" data-mp="33ud73oezc9ag">
            <span className="text-white/30" data-id="element-6880" data-mp="33vehdqo68rr4">isOnline: </span>
            <span className={isOnline ? 'text-green-400' : 'text-red-400'} data-id="element-6881" data-mp="2z34yuvpdytyv">
              {String(isOnline)}
            </span>
          </div>

          <div className="border-t border-white/5 pt-2 mt-2" data-id="element-6882" data-mp="2u3sb88qoxiv8" />

          <div className="text-white/60" data-id="element-6883" data-mp="3rk7l49b1c3wa">
            <span className="text-white/30" data-id="element-6884" data-mp="3rl0hqhga5d0p">pending offers: </span>
            <span className={debugInfo.pendingCount > 0 ? 'text-green-400 font-bold' : 'text-red-400'} data-id="element-6885" data-mp="3mspkjmcxc5bu">
              {debugInfo.pendingCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-6886" data-mp="7x5mtnk103l1">
            <span className="text-white/30" data-id="element-6887" data-mp="7w4cjlau3l4d">all offers (bypass): </span>
            <span className={debugInfo.allOffersCount > 0 ? 'text-yellow-400' : 'text-red-400'} data-id="element-6888" data-mp="bz0oit5vcizc">
              {debugInfo.allOffersCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-6889" data-mp="luc39bha1q73">
            <span className="text-white/30" data-id="element-6890" data-mp="qogalvugg08p">realtime: </span>
            <span className={debugInfo.realtimeActive ? 'text-green-400' : 'text-red-400'} data-id="element-6891" data-mp="vmi3t9llhfxw">
              {debugInfo.realtimeActive ? 'SUBSCRIBED' : 'NOT SUBSCRIBED'}
            </span>
          </div>
          <div className="text-white/60" data-id="element-6892" data-mp="14npoupz3oka1">
            <span className="text-white/30" data-id="element-6893" data-mp="r0fsqgjm9wn3">last fetch: </span>
            <span className="text-white/50" data-id="element-6894" data-mp="qymo16skdmhq">
              {debugInfo.lastFetchTime || 'never'}
            </span>
          </div>
          {debugInfo.lastError && <div className="text-red-400" data-id="element-6895" data-mp="10p7ici17g4uu">
              <span className="text-white/30" data-id="element-6896" data-mp="10sd69ty3l0oz">error: </span>
              {debugInfo.lastError}
            </div>}

          {/* Bypass offers detail */}
          {debugInfo.bypassOffers.length > 0 && <div className="border-t border-white/5 pt-2 mt-2" data-id="element-6897" data-mp="3zzwta0l5vqx">
              <p className="text-white/30 mb-1" data-id="element-6898" data-mp="41ku37eabssh">All offers (no filter):</p>
              {debugInfo.bypassOffers.map((o: any) => {
          const expired = new Date(o.expires_at).getTime() < Date.now();
          const expiresIn = Math.round((new Date(o.expires_at).getTime() - Date.now()) / 1000);
          return <div key={o.id} className="text-white/50 pl-2" data-id="element-6899" data-mp="1oes57c0hbm9e">
                    #{o.id}: {o.status}
                    {expired ? <span className="text-red-400" data-id="element-6900" data-mp="36fkmktrt582x"> EXPIRED</span> : <span className="text-green-400" data-id="element-6901" data-mp="3bagja77zojmd"> {expiresIn}s left</span>}
                  </div>;
        })}
            </div>}

          {debugInfo.allOffersCount === 0 && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 mt-2" data-id="element-6902" data-mp="3q1pb8mzd6h2o">
              <p className="text-red-400 font-bold" data-id="element-6903" data-mp="3u2su7icpkt84">⚠ RLS BLOCKING</p>
              <p className="text-red-400/70" data-id="element-6904" data-mp="3u5591mn66ngy">
                Bypass query returned 0 rows. Run RLS_DELIVERY_OFFERS_FIX.sql in
                Supabase.
              </p>
            </div>}

          <button onClick={onRefetch} className="w-full mt-2 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-lg py-2 text-white/50 hover:text-white/80 active:scale-95 transition-all" data-id="element-6905" data-mp="3l8mj8ja8jfol">
            <RefreshCw className="w-3.5 h-3.5" data-id="element-6906" data-mp="rhw5h2ezozwk" />
            Force Refetch
          </button>
        </div>}
    </div>;
}
// ─── Helper to extract job display data from real DB columns ─────
function getJobDisplayData(job: any) {
  if (!job) return {
    pickup: 'Restaurante',
    dropoff: 'Cliente',
    fee: 0,
    pickupLat: null,
    pickupLng: null,
    dropoffLat: null,
    dropoffLng: null,
    pickupName: 'Restaurante',
    dropoffName: 'Cliente',
    pickupAddress: '',
    dropoffAddress: '',
    customerPhone: null,
    mapsUrl: null
  };
  return {
    pickup: job.pickup_address_text || job.pickup_address || job.origin_address || job.pickup_location || job.restaurant_name || 'Restaurante',
    dropoff: job.dropoff_address_text || job.dropoff_address || job.destination_address || job.dropoff_location || job.customer_address || 'Cliente',
    fee: job.delivery_fee_estimated || job.delivery_fee || job.fee || job.estimated_fee || 0,
    pickupLat: job.pickup_lat || null,
    pickupLng: job.pickup_lng || null,
    dropoffLat: job.dropoff_lat || null,
    dropoffLng: job.dropoff_lng || null,
    pickupName: job.pickup_name || job.restaurant_name || 'Restaurante',
    dropoffName: job.dropoff_name || job.customer_name || 'Cliente',
    pickupAddress: job.pickup_address_text || job.pickup_address || job.origin_address || '',
    dropoffAddress: job.dropoff_address_text || job.dropoff_address || job.destination_address || '',
    customerPhone: job.customer_phone || job.dropoff_phone || job.phone || null,
    mapsUrl: job.maps_url || job.delivery_maps_url || null
  };
}
// ─── Main App ────────────────────────────────────────────────────
export function DriverAppPage() {
  const navigate = useNavigate();
  const {
    user,
    driverId,
    loading: authLoading,
    resolveError,
    logout,
    authDebug,
    retry: retryAuth
  } = useDriverAuth();
  const {
    isOnline,
    toggleStatus
  } = useDriverSession(driverId);
  const {
    offers,
    acceptOffer,
    rejectOffer,
    debugInfo,
    refetch
  } = useDriverOffers(driverId);
  const {
    activeTrip,
    updateTripStatus,
    clearTrip,
    refetchTrip
  } = useActiveTrip(driverId);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusToggling, setStatusToggling] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);
  const [deliveryComplete, setDeliveryComplete] = useState<{
    fee: number;
  } | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [navigationMode, setNavigationMode] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
    steps: RouteStep[];
  } | null>(null);
  // Alert on new offers (loud alarm + system notification)
  const {
    stopAlarm,
    warmUpAudio
  } = useOfferAlert(offers.length);
  // Auto-dismiss delivery complete toast
  useEffect(() => {
    if (!deliveryComplete) return;
    const timer = setTimeout(() => {
      setDeliveryComplete(null);
    }, 2500);
    return () => clearTimeout(timer);
  }, [deliveryComplete]);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/driver/login');
    }
  }, [user, authLoading, navigate]);
  if (authLoading) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-6907" data-mp="pznopcjnu0cv">
        <div className="w-10 h-10 border-3 border-[#006847] border-t-transparent rounded-full animate-spin" data-id="element-6908" data-mp="uq43p134pu1c" />
      </div>;
  }
  if (!driverId) {
    // Temporary error — show retryable state, NOT "Sin perfil"
    if (resolveError) {
      return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-6909" data-mp="l12npawl3cnd">
          <WifiOff className="w-16 h-16 text-amber-500/40 mb-6" data-id="element-6910" data-mp="71bh3v8ecyje" />
          <p className="text-white text-xl font-bold mb-2" data-id="element-6911" data-mp="b2d9pt7t6cce">Error de conexión</p>
          <p className="text-white/50 mb-8 max-w-xs" data-id="element-6912" data-mp="2xery6v6dklok">{resolveError}</p>
          <button onClick={retryAuth} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl flex items-center gap-2 mb-4" data-id="element-6913" data-mp="2xguv7z5y3f2s">
            <RefreshCw className="w-5 h-5" data-id="element-6914" data-mp="2ofwrlhzf59kq" />
            Reintentar
          </button>
          <button onClick={logout} className="text-white/30 text-sm py-2 px-6" data-id="element-6915" data-mp="2sfp2i1cabrt9">
            Cerrar sesión
          </button>
        </div>;
    }
    // Definitive — no driver profile found
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-6916" data-mp="3l671or9w191e">
        <Car className="w-16 h-16 text-white/20 mb-6" data-id="element-6917" data-mp="37av84n7gxom2" />
        <p className="text-white text-xl font-bold mb-2" data-id="element-6918" data-mp="3bd844jwey85n">
          Sin perfil de conductor
        </p>
        <p className="text-white/50 mb-8" data-id="element-6919" data-mp="2xoc1o7havs6">
          No tienes un perfil de conductor asignado a esta cuenta.
        </p>
        <button onClick={logout} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl" data-id="element-6920" data-mp="7qhh6d6v064s">
          Cerrar sesión
        </button>
      </div>;
  }
  const handleLogout = async () => {
    if (isOnline) await toggleStatus(false);
    await logout();
  };
  const handleToggleStatus = async () => {
    setStatusToggling(true);
    // Warm up audio + request notification permission on user gesture (required by mobile browsers)
    if (!isOnline) {
      warmUpAudio();
      requestNotificationPermission();
    }
    await toggleStatus(!isOnline);
    setStatusToggling(false);
  };
  const handleAccept = async (offerId: number) => {
    setActionLoading(true);
    setOfferError(null);
    stopAlarm();
    console.log('[DRIVER-UI] 🚀 Accepting offer:', offerId);
    const result = await acceptOffer(offerId);
    console.log('[DRIVER-UI] Accept result:', result);
    if (!result || result.success === false) {
      const errorMsg = result?.error || 'Error desconocido al aceptar';
      console.error('[DRIVER-UI] ❌ Accept failed:', errorMsg);
      setOfferError(errorMsg);
      setTimeout(() => setActionLoading(false), 1000);
      return;
    }
    console.log('[DRIVER-UI] ✅ Accept succeeded — refreshing state');
    setOfferError(null);
    // Immediately fetch active trip to transition into trip screen
    console.log('[DRIVER-UI] 🔄 Post-accept: fetching active trip...');
    await refetchTrip();
    console.log('[DRIVER-UI] 🔄 Post-accept: active trip fetch complete');
    console.log('[DRIVER-UI] 🔄 Post-accept: activeTrip will update on next render');
    setActionLoading(false);
  };
  const handleReject = async (offerId: number) => {
    setActionLoading(true);
    stopAlarm();
    await rejectOffer(offerId);
    setActionLoading(false);
  };
  // ROLLBACK: Route callbacks disabled (NavigationMap not rendered)
  // const handleRouteError = useCallback((error: string) => { ... }, [])
  // const handleRouteUpdate = useCallback((data) => { ... }, [])
  // const handleRetryRoute = () => { ... }
  const handleStatusChange = async (action: 'arrived' | 'picked_up' | 'en_route' | 'delivered', jobId: number) => {
    console.log('[DRIVER-UI] ═══════════════════════════════════');
    console.log('[DRIVER-UI] Button clicked:', action);
    console.log('[DRIVER-UI] Job ID:', jobId);
    console.log('[DRIVER-UI] Current activeTrip status:', activeTrip?.status);
    console.log('[DRIVER-UI] Order ID on trip:', activeTrip?.order_id);
    // Capture fee before clearing trip
    const currentFee = activeTrip ? activeTrip.delivery_fee_estimated || activeTrip.delivery_fee || activeTrip.fee || 0 : 0;
    const orderId = activeTrip?.order_id;
    setActionLoading(true);
    setTripError(null);
    // Step 1: Update delivery_jobs via hook (RPC + fallback)
    const result = await updateTripStatus(action, jobId);
    console.log('[DRIVER-UI] updateTripStatus result:', result);
    if (result && !result.success) {
      console.error('[DRIVER-UI] ❌ Status change failed:', result.error);
      const errorMsg = action === 'picked_up' ? 'No se pudo actualizar el pedido. Intenta de nuevo.' : action === 'delivered' ? 'No se pudo marcar como entregado. Intenta de nuevo.' : result.error || 'Error actualizando estado';
      setTripError(errorMsg);
      setActionLoading(false);
      return;
    }
    // Step 2: DIRECT update to public.orders (belt-and-suspenders)
    // This guarantees the orders table is updated even if syncOrderFromDeliveryJob failed silently
    if (orderId && (action === 'picked_up' || action === 'delivered')) {
      const orderUpdatePayload: Record<string, any> = {};
      if (action === 'picked_up') {
        console.log('[DRIVER-UI] 📝 Updating order to picked_up — order_id:', orderId);
        orderUpdatePayload.status = 'out_for_delivery';
        orderUpdatePayload.picked_up_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      } else if (action === 'delivered') {
        console.log('[DRIVER-UI] 📝 Updating order to delivered — order_id:', orderId);
        orderUpdatePayload.status = 'delivered';
        orderUpdatePayload.delivered_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      }
      console.log('[DRIVER-UI] 📝 Direct orders update payload:', orderUpdatePayload);
      const {
        data: orderUpdateResult,
        error: orderUpdateError
      } = await supabase.from('orders').update(orderUpdatePayload).eq('id', orderId).select('id, status');
      if (orderUpdateError) {
        console.error('[DRIVER-UI] ❌ Supabase update error on public.orders:', orderUpdateError.message);
        console.error('[DRIVER-UI] Error details:', JSON.stringify(orderUpdateError));
        // Don't block the flow — the delivery_jobs update already succeeded
      } else {
        console.log('[DRIVER-UI] ✅ Supabase update success on public.orders:', JSON.stringify(orderUpdateResult));
      }
    } else if (!orderId) {
      console.warn('[DRIVER-UI] ⚠️ No order_id on activeTrip — cannot update public.orders directly');
    }
    // Chain picked_up -> en_route automatically to simplify UX
    if (action === 'picked_up' && result?.success) {
      console.log('[DRIVER-UI] 🔗 Chaining picked_up -> en_route');
      const chainResult = await updateTripStatus('en_route', jobId);
      if (chainResult && !chainResult.success) {
        console.warn('[DRIVER-UI] ⚠️ Chained en_route failed, but picked_up succeeded:', chainResult.error);
      }
      // ROLLBACK: Navigation mode activation disabled
      console.log('[DRIVER-UI] 🗺️ Picked up — step 3 active');
    }
    // On successful delivery: haptic feedback + show success
    if (action === 'delivered' && result?.success) {
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100]);
      }
      console.log('[DRIVER-UI] ✅ Delivery complete — showing success');
      // Clear trip and show success toast
      clearTrip();
      setTripError(null);
      setActionLoading(false);
      setDeliveryComplete({
        fee: currentFee
      });
      // Refresh offers in background so driver is ready for next trip
      refetch();
      return;
    }
    setActionLoading(false);
    console.log('[DRIVER-UI] ═══════════════════════════════════');
  };
  // Priority offer = first pending offer
  const priorityOffer = offers.length > 0 ? offers[0] : null;
  // ─── SCREEN: OFFLINE ──────────────────────────────────────────
  if (!isOnline) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-6921" data-mp="3cwrq8e0u6nik">
        {/* Mini header */}
        <div className="flex items-center justify-between px-5 py-4" data-id="element-6922" data-mp="emfb1z0b45c">
          <div className="flex items-center gap-2" data-id="element-6923" data-mp="dbcp5has2ft">
            <Car className="w-5 h-5 text-white/30" data-id="element-6924" data-mp="56o1bflz898k" />
            <span className="font-bold text-white/50 text-sm" data-id="element-6925" data-mp="3mme1tirr5vec">
              NOIZA Driver
            </span>
          </div>
          <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-6926" data-mp="3rkxk99750p1y">
            <LogOut className="w-5 h-5" data-id="element-6927" data-mp="j5npkm5kum47" />
          </button>
        </div>

        {/* Big offline state */}
        <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-6928" data-mp="o4gtbmv2ayr6">
          <div className="w-32 h-32 rounded-full bg-[#1A1A1A] border-4 border-white/10 flex items-center justify-center mb-8" data-id="element-6929" data-mp="o2e3b2twt5mg">
            <WifiOff className="w-16 h-16 text-white/20" data-id="element-6930" data-mp="e61bkahxd1uc" />
          </div>
          <h2 className="text-3xl font-black text-white/30 mb-2" data-id="element-6931" data-mp="e8douacqmo12">
            DESCONECTADO
          </h2>
          <p className="text-white/30 text-center mb-12" data-id="element-6932" data-mp="2yws3exj7fk7e">
            No estás recibiendo viajes
          </p>

          <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full max-w-xs py-6 rounded-2xl bg-[#006847] text-white font-black text-2xl flex items-center justify-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-6933" data-mp="1gywv4u935eu6">
            {statusToggling ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-6934" data-mp="1lwf3aifa3q14" /> : <>
                <Power className="w-8 h-8" data-id="element-6935" data-mp="y6ktyau64jlm" />
                CONECTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: DELIVERY COMPLETE (brief success) ─────────────────
  if (deliveryComplete) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center text-white px-8" data-id="element-6936" data-mp="1297mkdyny8jv">
        <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-6 animate-[scale-in_0.3s_ease-out]" data-id="element-6937" data-mp="ejdavzq2yq17">
          <CheckCircle2 className="w-14 h-14 text-green-400" data-id="element-6938" data-mp="ah9xh5jupbda" />
        </div>
        <h2 className="text-3xl font-black text-white mb-2" data-id="element-6939" data-mp="33s9p56upcxxc">
          ¡Entrega completada!
        </h2>
        {deliveryComplete.fee > 0 && <p className="text-2xl font-bold text-green-400 mb-2" data-id="element-6940" data-mp="2uyetcpfh0jud">
            {formatCurrency(deliveryComplete.fee, '₡')}
          </p>}
        <p className="text-white/40 text-sm" data-id="element-6941" data-mp="2q14zmxl1tm0q">Volviendo a buscar viajes...</p>
        <div className="mt-6 w-48 h-1 bg-white/10 rounded-full overflow-hidden" data-id="element-6942" data-mp="3mot3cakd5c0r">
          <div className="h-full bg-green-500 rounded-full animate-[shrink_2.5s_linear_forwards]" data-id="element-6943" data-mp="3inpqlwxm3w3r" />
        </div>
      </div>;
  }
  // ─── SCREEN: ACTIVE TRIP (highest priority) ───────────────────
  if (activeTrip) {
    const tripData = getJobDisplayData(activeTrip);
    let step = 1;
    let progress = 33;
    let title = 'PASO 1 DE 3';
    let headerBadge = '🏪 Vas al restaurante';
    let destinationLabel = 'Dirección del restaurante';
    let destinationName = tripData.pickupName;
    let destinationAddress = tripData.pickupAddress;
    let mapLat = tripData.pickupLat;
    let mapLng = tripData.pickupLng;
    let mapsUrl = tripData.mapsUrl;
    if (activeTrip.status === 'arrived_pickup') {
      step = 2;
      progress = 66;
      title = 'PASO 2 DE 3';
      headerBadge = '📦 Ya llegaste al restaurante';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    } else if (activeTrip.status === 'picked_up' || activeTrip.status === 'en_route') {
      step = 3;
      progress = 100;
      title = 'PASO 3 DE 3';
      headerBadge = '🚗 Camino al cliente';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    }
    return <div className="bg-[#0B0B0B] flex flex-col text-white" style={{
      minHeight: '100dvh'
    }} data-id="element-6944" data-mp="ig0yvvxm019k">
        {/* Header */}
        <div className="bg-[#141414] border-b border-white/5 px-5 py-3 flex items-center justify-between" style={{
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }} data-id="element-6945" data-mp="dih8xyrju01s">
          <div className="flex flex-col" data-id="element-6946" data-mp="ndssqvhtdxge">
            <span className="font-bold text-white/50 text-[10px] tracking-widest mb-0.5" data-id="element-6947" data-mp="11ed8fisfnd5o">
              {title}
            </span>
            <span className="font-black text-white text-sm" data-id="element-6948" data-mp="wf0p380rso7u">{headerBadge}</span>
          </div>
          <span className="text-2xl font-black text-[#00a870]" data-id="element-6949" data-mp="1b3dajb28eoo3">
            {formatCurrency(tripData.fee, '₡')}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 w-full bg-[#1A1A1A]" data-id="element-6950" data-mp="1k4v15nd9m8r1">
          <div className="h-full bg-[#00a870] transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-6951" data-mp="1k5mn06h28s44" />
        </div>

        {/* Error Banner */}
        {tripError && <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-6952" data-mp="23roc30tkiqgs">
            <span className="text-red-400 text-sm font-mono flex-1" data-id="element-6953" data-mp="1pqlq0o2kpg5v">
              {tripError}
            </span>
            <button onClick={() => setTripError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-6954" data-mp="1uox1124ltwpo">
              ✕
            </button>
          </div>}

        {/* Main Content */}
        {step === 3 || navigationMode /* ── NAVIGATION MODE — full-screen real-time map ── */ ? <div className="flex-1 flex flex-col overflow-hidden" data-id="element-6955" data-mp="1b27qon4d89u2">
            <div className="flex-1 relative" data-id="element-6956" data-mp="1b2qwx6knl9s9">
              {mapLat && mapLng ? <MapErrorBoundary fallbackLat={mapLat} fallbackLng={mapLng} data-id="element-6957" data-mp="x3837isla0zh">
                  <NavigationMap destinationLat={mapLat} destinationLng={mapLng} destinationName={destinationName} destinationAddress={destinationAddress} compact={false} data-id="element-6958" data-mp="120rec9tzpxn7" />
                </MapErrorBoundary> : <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-6959" data-mp="1vkstriwugg7r" />}
            </div>

            {/* Bottom Action Bar */}
            <div className="bg-[#141414] border-t border-white/10 px-4 pt-4 shrink-0" style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))'
        }} data-id="element-6960" data-mp="1lozevsvnkamt">
              <div className="space-y-3" data-id="element-6961" data-mp="1cpvvenskpt7s">
                {step === 1 && <button disabled={actionLoading} onClick={() => handleStatusChange('arrived', activeTrip.id)} className="w-full py-6 bg-white text-black font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-6962" data-mp="17qsp700uxgy8">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-6963" data-mp="3ejiaw4z0dwj9" /> : <>
                        <MapPin className="w-8 h-8" data-id="element-6964" data-mp="3obekodagjb3b" /> YA LLEGUÉ
                      </>}
                  </button>}

                {step === 2 && <button disabled={actionLoading} onClick={() => handleStatusChange('picked_up', activeTrip.id)} className="w-full py-6 bg-[#006847] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#006847]/20" data-id="element-6965" data-mp="2qpz1jle1e03i">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-6966" data-mp="jvd6vuiu9bjp" /> : <>
                        <Package className="w-8 h-8" data-id="element-6967" data-mp="2gznclncif3w7" /> YA RECOGÍ EL PEDIDO
                      </>}
                  </button>}

                {step === 3 && <button disabled={actionLoading} onClick={() => handleStatusChange('delivered', activeTrip.id)} className="w-full py-6 bg-[#00a870] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#00a870]/30" data-id="element-6968" data-mp="2pw4o4rosd3sh">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-6969" data-mp="226tj2y0ekn8j" /> : <>
                        <CheckCircle2 className="w-8 h-8" data-id="element-6970" data-mp="19k8u247egn85" /> PEDIDO ENTREGADO
                      </>}
                  </button>}

                {mapLat && mapLng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLng}&travelmode=driving`} target="_blank" rel="noreferrer" className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-6971" data-mp="3py2giosn3csj">
                    <MapIcon className="w-4 h-4" data-id="element-6972" data-mp="csapu6fkhu6j" /> Abrir en Google Maps
                  </a>}

                {step !== 3 && <button onClick={() => setNavigationMode(false)} className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-6973" data-mp="3ux5mcbgg1n1k">
                    <MapIcon className="w-4 h-4" data-id="element-6974" data-mp="2oi4rk935nawk" /> Ver resumen
                  </button>}
              </div>
            </div>
          </div> /* ── STATIC MODE (Steps 1 & 2) — overview with "Iniciar viaje" button ── */ : <div className="flex-1 flex flex-col overflow-y-auto" data-id="element-6975" data-mp="366qnqpvurica">
            {/* Route Map — shows driver + destination + polyline via OSRM */}
            <div className="px-4 pt-4 h-64" data-id="element-6976" data-mp="318xfzsewig9w">
              {mapLat && mapLng ? <MapErrorBoundary fallbackLat={mapLat} fallbackLng={mapLng} data-id="element-6977" data-mp="2q9touohdbxa">
                  <StaticRouteMap destinationLat={mapLat} destinationLng={mapLng} onRouteData={setRouteInfo} data-id="element-6978" data-mp="3twkzf44fr0uy" />
                </MapErrorBoundary> : <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-6979" data-mp="3k5yohn7nyyil" />}
            </div>

            {/* ETA / Distance Card */}
            {routeInfo && <div className="px-4 pt-3" data-id="element-6980" data-mp="goq714gum0oe">
                <div className="bg-[#141414] border border-[#00a870]/20 rounded-2xl p-4 flex items-center justify-around" data-id="element-6981" data-mp="gnoneblro5v2">
                  <div className="flex flex-col items-center" data-id="element-6982" data-mp="szqyejug2wrz">
                    <Clock className="w-5 h-5 text-[#00a870] mb-1" data-id="element-6983" data-mp="t0aerw4g5d3w" />
                    <p className="text-2xl font-black text-white tabular-nums" data-id="element-6984" data-mp="xy2br4jy7pey">
                      {Math.max(1, Math.round(routeInfo.duration / 60))}
                    </p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-6985" data-mp="17xt532wrvydg">
                      min
                    </p>
                  </div>
                  <div className="w-px h-12 bg-white/10" data-id="element-6986" data-mp="1gpl8wurak992" />
                  <div className="flex flex-col items-center" data-id="element-6987" data-mp="1gq4s0esoh3oo">
                    <Navigation className="w-5 h-5 text-[#00a870] mb-1" data-id="element-6988" data-mp="1lp7qfge5kbno" />
                    <p className="text-2xl font-black text-white tabular-nums" data-id="element-6989" data-mp="1qnrcrhwnrpgk">
                      {routeInfo.distance >= 1000 ? (routeInfo.distance / 1000).toFixed(1) : Math.round(routeInfo.distance)}
                    </p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-6990" data-mp="ddnl419t2doa">
                      {routeInfo.distance >= 1000 ? 'km' : 'm'}
                    </p>
                  </div>
                </div>
              </div>}

            {/* Destination Card */}
            <div className="px-4 pt-4" data-id="element-6991" data-mp="r99862whane2">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-center" data-id="element-6992" data-mp="3qn1pvgfxh4rc">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-6993" data-mp="3vkuxmdwvq6tq">
                  {step === 1 ? <MapPin className="w-4 h-4 text-white/40" data-id="element-6994" data-mp="3giu9o50lu15" /> : <Navigation className="w-4 h-4 text-white/40" data-id="element-6995" data-mp="8gx3vam9lc1a" />}
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold" data-id="element-6996" data-mp="1ex8tuoexnnls">
                    {destinationLabel}
                  </p>
                </div>
                <p className="text-2xl font-black leading-tight mb-1" data-id="element-6997" data-mp="1jxn5shjnnhp3">
                  {destinationName || 'Destino'}
                </p>
                {destinationAddress && <p className="text-white/50 text-sm leading-relaxed" data-id="element-6998" data-mp="1p2qy0nyfvnp9">
                    {destinationAddress}
                  </p>}
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-4" data-id="element-6999" data-mp="1g6j8yx801ctx" />

            {/* Actions */}
            <div className="px-4 pt-4 space-y-3" style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))'
        }} data-id="element-7000" data-mp="1bakj1570vmre">
              {/* INICIAR VIAJE — activates real-time navigation */}
              {mapLat && mapLng && <button onClick={() => setNavigationMode(true)} className="w-full py-7 bg-[#006847] text-white font-black text-2xl rounded-3xl flex justify-center items-center gap-3 active:scale-95 transition-transform shadow-xl shadow-[#006847]/30" data-id="element-7001" data-mp="2vp4a933rflsv">
                  <Navigation className="w-8 h-8" data-id="element-7002" data-mp="trkf5a9yh929" /> INICIAR VIAJE
                </button>}

              {step === 1 && <button disabled={actionLoading} onClick={() => handleStatusChange('arrived', activeTrip.id)} className="w-full py-5 bg-white text-black font-black text-xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-7003" data-mp="g177l90z7wdg">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-7004" data-mp="yplv3mc34s9i" /> : <>
                      <MapPin className="w-7 h-7" data-id="element-7005" data-mp="13of71abq4b7k" /> YA LLEGUÉ
                    </>}
                </button>}

              {step === 2 && <button disabled={actionLoading} onClick={() => handleStatusChange('picked_up', activeTrip.id)} className="w-full py-5 bg-white/10 border border-white/20 text-white font-black text-xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-7006" data-mp="3og90p0bv6gfb">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-7007" data-mp="3kzcubnqq01jc" /> : <>
                      <Package className="w-7 h-7" data-id="element-7008" data-mp="2x90i40ofzimy" /> YA RECOGÍ EL PEDIDO
                    </>}
                </button>}

              {step === 3 && <button disabled={actionLoading} onClick={() => handleStatusChange('delivered', activeTrip.id)} className="w-full py-6 bg-[#00a870] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#00a870]/30" data-id="element-7009" data-mp="36z23ultbooyp">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-7010" data-mp="1cpetxcuhbuyw" /> : <>
                      <CheckCircle2 className="w-7 h-7" data-id="element-7011" data-mp="1ls7iv9g6vulk" /> PEDIDO ENTREGADO
                    </>}
                </button>}

              {mapLat && mapLng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLng}&travelmode=driving`} target="_blank" rel="noreferrer" className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-7012" data-mp="1qkqtpynhsbmo">
                  <MapIcon className="w-4 h-4" data-id="element-7013" data-mp="29ffltkq5arkf" /> Abrir en Google Maps
                </a>}
            </div>
          </div>}
      </div>;
  }
  // ─── SCREEN: FULL-SCREEN OFFER (priority) ─────────────────────
  if (priorityOffer) {
    const job = priorityOffer.delivery_jobs;
    const jobData = getJobDisplayData(job);
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-7014" data-mp="2sq5daclsciq">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[#141414] border-b border-white/5" data-id="element-7015" data-mp="3u0m6fbmp5ofb">
          <div className="flex items-center gap-2" data-id="element-7016" data-mp="3pzsi4h5ce7lw">
            <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-7017" data-mp="qjm3ep7nk3yf" />
            <span className="font-bold text-sm text-[#00a870]" data-id="element-7018" data-mp="ljzq6frtqnpm">
              NUEVA OFERTA
            </span>
          </div>
          <button onClick={handleToggleStatus} className="text-xs text-white/30 font-bold py-1.5 px-3 border border-white/10 rounded-lg" data-id="element-7019" data-mp="hhwafey4gwyj">
            Desconectar
          </button>
        </div>

        <div className="flex-1 flex flex-col px-5 py-6" data-id="element-7020" data-mp="329em6fjmzom1">
          {/* Error Banner */}
          {offerError && <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-7021" data-mp="2x88t2gm2dzbz">
              <span className="text-red-400 text-sm font-bold flex-1" data-id="element-7022" data-mp="2yowfd8yd7zrh">
                {offerError}
              </span>
              <button onClick={() => setOfferError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-7023" data-mp="2pqav99d8wcol">
                ✕
              </button>
            </div>}

          {/* Payment - Hero */}
          <div className="text-center mb-8" data-id="element-7024" data-mp="2fywtsw1qh2st">
            <p className="text-6xl font-black text-white mb-1" data-id="element-7025" data-mp="2krg51mcy7lug">
              {formatCurrency(jobData.fee, '₡')}
            </p>
            <p className="text-white/40 text-sm" data-id="element-7026" data-mp="263cxw9n66442">Pago estimado</p>
          </div>

          {/* Countdown */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 mb-6 flex justify-center" data-id="element-7027" data-mp="2b1o7cj9jyclr">
            <CountdownTimer expiresAt={priorityOffer.expires_at} data-id="element-7028" data-mp="1x1vikthl66gf" />
          </div>

          {/* Route */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 space-y-5 mb-6" data-id="element-7029" data-mp="21zna3mebjqk3">
            <div className="flex gap-4 items-start" data-id="element-7030" data-mp="1iewun8hxtu6g">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0 mt-0.5" data-id="element-7031" data-mp="1dg3knq1v0lb1">
                <MapPin className="w-5 h-5 text-white/50" data-id="element-7032" data-mp="1rge6ufs85bpv" />
              </div>
              <div data-id="element-7033" data-mp="1nd859lg248pq">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-7034" data-mp="1ncoq2chsjyfe">
                  Recoger
                </p>
                <p className="text-base font-bold" data-id="element-7035" data-mp="21bod5fpno59g">{jobData.pickup}</p>
              </div>
            </div>
            <div className="border-t border-white/5" data-id="element-7036" data-mp="1wcbqow2p507e" />
            <div className="flex gap-4 items-start" data-id="element-7037" data-mp="2b3lrgvbyndce">
              <div className="w-10 h-10 rounded-full bg-[#006847]/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-7038" data-mp="2b556kilvricl">
                <Navigation className="w-5 h-5 text-[#006847]" data-id="element-7039" data-mp="2k3qvcvhy3tlt" />
              </div>
              <div data-id="element-7040" data-mp="2f4xhh1yqy0l4">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-7041" data-mp="2f5flwlvkbl44">
                  Entregar
                </p>
                <p className="text-base font-bold" data-id="element-7042" data-mp="3mgbkche8qbcr">{jobData.dropoff}</p>
              </div>
            </div>
          </div>

          {/* Remaining offers indicator */}
          {offers.length > 1 && <p className="text-center text-xs text-white/30 mb-4" data-id="element-7043" data-mp="3vjcle3o91c9k">
              +{offers.length - 1}{' '}
              {offers.length - 1 === 1 ? 'oferta más' : 'ofertas más'} en cola
            </p>}
        </div>

        {/* Action Buttons - Fixed Bottom */}
        <div className="p-5 pb-8 bg-[#141414] border-t border-white/5 flex gap-3" data-id="element-7044" data-mp="37ryr3s6jf6ly">
          <button disabled={actionLoading} onClick={() => handleReject(priorityOffer.id)} className="flex-1 py-6 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/60 font-black text-xl flex justify-center items-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-7045" data-mp="3crtjy19vi69n">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-7046" data-mp="2ozwcsnhdc2ev" /> : <>
                <X className="w-7 h-7" data-id="element-7047" data-mp="2hgdj9nai0qs1" /> NO
              </>}
          </button>
          <button disabled={actionLoading} onClick={() => handleAccept(priorityOffer.id)} className="flex-[2] py-6 rounded-2xl bg-[#006847] text-white font-black text-xl flex justify-center items-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-7048" data-mp="2ck6a7lmt2z5v">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-7049" data-mp="1ts3tkaa5zivm" /> : <>
                <Check className="w-7 h-7" data-id="element-7050" data-mp="1ouke571qd023" /> ACEPTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: ONLINE, WAITING FOR OFFERS ───────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-7051" data-mp="1fskxifumnxzo">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4" data-id="element-7052" data-mp="1aujefux7byo7">
        <div className="flex items-center gap-3" data-id="element-7053" data-mp="jrf6qdu4hl93">
          <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-7054" data-mp="jub5239vuby2" />
          <span className="font-bold text-sm text-[#00a870]" data-id="element-7055" data-mp="eslrogz6tm6t">ONLINE</span>
        </div>
        <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-7056" data-mp="tpcpwmx4kruu">
          <LogOut className="w-5 h-5" data-id="element-7057" data-mp="oqrsev99cma3" />
        </button>
      </div>

      {/* Big online indicator */}
      <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-7058" data-mp="11792b96zn0a">
        <div className="relative mb-8" data-id="element-7059" data-mp="3s81ndaswqnzb">
          <div className="w-40 h-40 rounded-full bg-[#006847]/5 flex items-center justify-center" data-id="element-7060" data-mp="3s92wvau9z2ex">
            <div className="w-28 h-28 rounded-full bg-[#006847]/10 flex items-center justify-center animate-pulse" data-id="element-7061" data-mp="aslawpom8ywl">
              <div className="w-20 h-20 rounded-full bg-[#006847]/20 flex items-center justify-center" data-id="element-7062" data-mp="5z8sixaj5oca">
                <Wifi className="w-10 h-10 text-[#00a870]" data-id="element-7063" data-mp="3eesgbgsfmejy" />
              </div>
            </div>
          </div>
          {/* Ping animation */}
          <div className="absolute inset-0 rounded-full border-2 border-[#006847]/20 animate-ping" data-id="element-7064" data-mp="1ozrf4vbkniu7" />
        </div>

        <h2 className="text-2xl font-black text-white mb-2" data-id="element-7065" data-mp="1tqhlaartnppy">Buscando viajes</h2>
        <p className="text-white/40 text-center text-sm mb-4" data-id="element-7066" data-mp="1f1liot8jb4wq">
          Mantén la app abierta para recibir ofertas
        </p>
      </div>

      {/* Debug Panel */}
      {/* Debug panel hidden — uncomment to re-enable:
                                                                                  <DriverDebugPanel
                                                                                    user={user}
                                                                                    driverId={driverId}
                                                                                    authDebug={authDebug}
                                                                                    offers={offers}
                                                                                    debugInfo={debugInfo}
                                                                                    isOnline={isOnline}
                                                                                    onRefetch={refetch}
                                                                                  />
                                                                                  */}

      {/* Disconnect button at bottom */}
      <div className="p-5 pb-8" data-id="element-7067" data-mp="2b9814nnem6c1">
        <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full py-5 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/50 font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-7068" data-mp="268trj165mobw">
          {statusToggling ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-7069" data-mp="3vmgrati2zbq3" /> : <>
              <Power className="w-6 h-6" data-id="element-7070" data-mp="3qpqce9i0im69" />
              DESCONECTAR
            </>}
        </button>
      </div>
    </div>;
}